import { useMemo, useState } from "react";
import { Badge, Code, Group, Select, Stack, Table, Text, TextInput } from "@mantine/core";
import { useQuery } from "@tanstack/react-query";
import type { AdminInviteClaimList } from "@mip-tg-bot-turntable/platform-api";
import { adminGet } from "../../api.ts";
import { AdminTable } from "../../components/AdminTable.tsx";
import { TablePagination } from "../../components/TablePagination.tsx";
import { useClientPagination } from "../../hooks/useClientPagination.ts";

const REASON_ZH: Record<string, string> = {
  no_invitee_deposit_yet: "下级尚无充值到账记录",
  granted: "已发券",
  granted_no_spins: "规则禁用或券数=0，不发券",
  provider_error: "WG 接口调用失败",
};

const renderReason = (raw: string | null): string => {
  if (raw === null) return "-";
  if (raw.startsWith("invitee_amount_below_threshold:")) {
    const detail = raw.slice("invitee_amount_below_threshold:".length);
    return `下级首充金额未达门槛（${detail}）`;
  }
  return REASON_ZH[raw] ?? raw;
};

export function InviteClaimsTab({ botId }: { readonly botId: string }) {
  const { data, isLoading } = useQuery({
    queryKey: ["admin-invite-claims", botId],
    queryFn: () => adminGet<AdminInviteClaimList>(`/bots/${botId}/invite-claims`),
  });

  const [statusFilter, setStatusFilter] = useState<"all" | "pending_deposit" | "granted">("all");
  const [search, setSearch] = useState("");

  const items = data?.items ?? [];
  const keyword = search.trim().toLowerCase();
  const filtered = useMemo(() => {
    return items.filter((r) => {
      if (statusFilter !== "all" && r.status !== statusFilter) return false;
      if (keyword === "") return true;
      const username = (r.inviterTgUsername ?? "").toLowerCase();
      const tgId = String(r.inviterTgUserId);
      const account = r.inviteeAccount.toLowerCase();
      const site = r.inviteeSiteCode.toLowerCase();
      return (
        username.includes(keyword) ||
        tgId.includes(keyword) ||
        account.includes(keyword) ||
        site.includes(keyword)
      );
    });
  }, [items, statusFilter, keyword]);

  const resetKey = `${statusFilter}|${keyword}`;
  const { pageItems, page, setPage, pageSize, total, totalPages } = useClientPagination(
    filtered,
    resetKey,
  );

  if (isLoading) return <Text c="dimmed">加载中</Text>;
  if (items.length === 0) {
    return <Text c="dimmed">暂无邀请申报记录</Text>;
  }

  return (
    <Stack gap="sm">
      <Text size="sm" c="dimmed">
        本页最多 30 条邀请申报。pending 由 workers 每 15 分钟自动刷新一次，也可在 Mini App
        手动刷新。
      </Text>
      <Group gap="sm" wrap="wrap">
        <Select
          size="xs"
          label="状态"
          data={[
            { label: "全部", value: "all" },
            { label: "等待首充", value: "pending_deposit" },
            { label: "已发券", value: "granted" },
          ]}
          value={statusFilter}
          onChange={(v) => setStatusFilter((v ?? "all") as "all" | "pending_deposit" | "granted")}
          style={{ width: 160 }}
          clearable={false}
        />
        <TextInput
          size="xs"
          label="搜索"
          placeholder="邀请人 username / tg id / 下级 account / site code"
          value={search}
          onChange={(e) => setSearch(e.currentTarget.value)}
          style={{ minWidth: 320, flex: 1 }}
        />
      </Group>
      <AdminTable minWidth={900}>
        <Table.Thead>
          <Table.Tr>
            <Table.Th>创建时间</Table.Th>
            <Table.Th>状态</Table.Th>
            <Table.Th>邀请人</Table.Th>
            <Table.Th>下级账号</Table.Th>
            <Table.Th>发券</Table.Th>
            <Table.Th>最近检查原因</Table.Th>
            <Table.Th>检查时间</Table.Th>
          </Table.Tr>
        </Table.Thead>
        <Table.Tbody>
          {pageItems.length === 0 ? (
            <Table.Tr>
              <Table.Td colSpan={7}>
                <Text c="dimmed" size="sm" ta="center">
                  无匹配记录
                </Text>
              </Table.Td>
            </Table.Tr>
          ) : (
            pageItems.map((r) => (
              <Table.Tr key={r.id}>
                <Table.Td>{new Date(r.createdAt).toLocaleString()}</Table.Td>
                <Table.Td>
                  <Badge color={r.status === "granted" ? "green" : "yellow"} variant="light">
                    {r.status === "granted" ? "已发券" : "等待首充"}
                  </Badge>
                </Table.Td>
                <Table.Td>
                  <Stack gap={0}>
                    <Text size="sm">
                      {r.inviterTgUsername !== null && r.inviterTgUsername !== ""
                        ? `@${r.inviterTgUsername}`
                        : "-"}
                    </Text>
                    <Text size="xs" c="dimmed">
                      tg {r.inviterTgUserId}
                    </Text>
                  </Stack>
                </Table.Td>
                <Table.Td>
                  <Stack gap={0}>
                    <Text size="sm">{r.inviteeAccount}</Text>
                    <Text size="xs" c="dimmed">
                      idx {r.inviteePlatformUserIdx} / {r.inviteeSiteCode}
                    </Text>
                  </Stack>
                </Table.Td>
                <Table.Td>{r.spinsGranted ?? "-"}</Table.Td>
                <Table.Td>
                  <Stack gap={2}>
                    <Text size="sm">{renderReason(r.lastCheckReason)}</Text>
                    {r.lastCheckReason !== null && (
                      <Code style={{ fontSize: 10 }}>{r.lastCheckReason}</Code>
                    )}
                  </Stack>
                </Table.Td>
                <Table.Td>
                  {r.lastCheckedAt !== null ? new Date(r.lastCheckedAt).toLocaleString() : "-"}
                </Table.Td>
              </Table.Tr>
            ))
          )}
        </Table.Tbody>
      </AdminTable>
      <TablePagination
        page={page}
        pageSize={pageSize}
        total={total}
        totalPages={totalPages}
        onPageChange={setPage}
      />
    </Stack>
  );
}
